<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  getLicenseTypes,
  LicenseType,
  deleteLicenseType,
  stateLicenseType
} from '@/app/core/services/license-types'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const loading = ref(false)

/* Datos */
const search = ref()
const tableLicenseType = ref(null)
const licenseTypeForDelete = ref()
const licenseTypeUpdateState = ref()
const descriptionModal = ref('')
const acceptQuestion = ref('')
const isDelete = ref(false)

/* Datos componente table */
const columns = ref([
  {
    name: 'code',
    title: t('licenseTypes.list.code'),
    table: 'license_types.code'
  },
  {
    name: 'name',
    title: t('licenseTypes.list.name'),
    table: 'license_types.name'
  },
  {
    name: 'entity',
    title: t('licenseTypes.list.entity'),
    table: 'entities.name'
  },
  {
    name: 'state',
    title: t('licenseTypes.list.state'),
    table: 'license_types.is_inactive'
  },
  {
    name: 'updated_at',
    title: t('licenseTypes.list.lastUpdate'),
    table: 'license_types.updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<LicenseType[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createLicenseType = () => {
  void router.push({ name: 'CreateLicenseType' })
}
const updateLicenseType = (id: any) => {
  void router.push({ name: 'UpdateLicenseType', params: { id } })
}
const getLicenseType = (id: any) => {
  void router.push({ name: 'GetLicenseType', params: { id } })
}

/* Cambiar estado del tipo de licencia */
const changeStateLicenseType = (values: any) => {
  const text = values.is_inactive
    ? t('table.action.activate')
    : t('table.action.deactivate')
  licenseTypeUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.is_inactive }
  ]
  showModal.value = true
  titleModal.value = t('licenseTypes.list.actionStatus', {
    status: text,
    licenseType: values.name as string
  })
  descriptionModal.value = ''
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  isDelete.value = false
}
const changeStateConfirm = async () => {
  showModal.value = false
  await stateLicenseType(
    licenseTypeUpdateState.value[0].id,
    licenseTypeUpdateState.value[0].is_inactive as boolean
  )
    .then(response => {
      notifications.addNotification({
        title: !licenseTypeUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('licenseTypes.notification.changeStatus', {
          licenseType: licenseTypeUpdateState.value[0].name as string,
          status: !licenseTypeUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void licenseTypes()
    })
    .catch(async (error: any) => {
      notificationError(error, 'licenseTypes', t, notifications, loading)
    })
}

/* Eliminar tipo de licencia */
const removeLicenseType = (values: any) => {
  showModal.value = true
  titleModal.value = t('licenseTypes.list.actionStatus', {
    status: t('table.action.delete'),
    licenseType: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  licenseTypeForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteLicenseType(licenseTypeForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('licenseTypes.notification.action', [
          t('notification.wasRemoved')
        ]),
        type: 'success',
        time: 10000
      })
      void licenseTypes()
    })
    .catch(async (error: any) => {
      notificationError(error, 'licenseTypes', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar tipos de licencia */
const licenseTypes = async () => {
  loading.value = true
  try {
    const data: any = await getLicenseTypes(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'licenseTypes', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('licenseTypes.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('licenseTypes.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="licenseTypes" />
          <DBtn
            @click.prevent="licenseTypes"
            v-html="t('form.button.search')" />
        </div>
        <DBtn
          v-if="store.hasPermission('license_type:create')"
          :icon="'PlusIcon'"
          type="button"
          @click="createLicenseType()">
          {{
            t('form.button.create', { form: t('licenseTypes.list.title', 0) })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableLicenseType"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), licenseTypes()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), licenseTypes()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('table.action.inactive')
              : t('table.action.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('license_type:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getLicenseType(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              !data.is_inactive && store.hasPermission('license_type:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateLicenseType(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('license_type:enable')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('licenseTypes.list.activate')
                : t('licenseTypes.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateLicenseType(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              data.is_inactive && store.hasPermission('license_type:delete')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeLicenseType(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
